import { nextTick, onBeforeUnmount, ref, type Ref } from "vue";

interface AnchoredPopoverOptions {
  matchWidth?: boolean;
  alignRight?: boolean;
  maxHeight?: number;
}

export const useAnchoredPopover = (options: AnchoredPopoverOptions = {}) => {
  const rootRef: Ref<HTMLElement | null> = ref(null);
  const triggerRef: Ref<HTMLElement | null> = ref(null);
  const panelRef: Ref<HTMLElement | null> = ref(null);
  const isOpen = ref(false);
  const panelStyle = ref<Record<string, string>>({});

  const updatePosition = (contentHeight = 0): void => {
    if (!triggerRef.value) {
      return;
    }

    const rect = triggerRef.value.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - 12;
    const spaceAbove = rect.top - 12;
    const neededHeight = Math.min(options.maxHeight ?? 280, contentHeight || 180);
    const opensUpwards = spaceBelow < neededHeight && spaceAbove > spaceBelow;
    const maxHeight = Math.min(options.maxHeight ?? 280, opensUpwards ? spaceAbove : spaceBelow);

    panelStyle.value = {
      maxHeight: `${maxHeight}px`,
      ...(options.matchWidth ? { width: `${rect.width}px` } : { minWidth: `${rect.width}px` }),
      ...(options.alignRight
        ? { right: `${window.innerWidth - rect.right}px` }
        : { left: `${rect.left}px` }),
      ...(opensUpwards
        ? { bottom: `${window.innerHeight - rect.top + 6}px` }
        : { top: `${rect.bottom + 6}px` }),
      transformOrigin: `${opensUpwards ? "bottom" : "top"} ${options.alignRight ? "right" : "left"}`,
      "--ui-pop-shift": opensUpwards ? "6px" : "-6px",
    };
  };

  const onDocumentPointerDown = (event: Event): void => {
    const target = event.target as Element;

    if (
      rootRef.value?.contains(target) ||
      panelRef.value?.contains(target) ||
      target.closest?.(".ui-select__list")
    ) {
      return;
    }

    close();
  };

  const onViewportChange = (event: Event): void => {
    if (event.target instanceof Node && panelRef.value?.contains(event.target)) {
      return;
    }

    close();
  };

  const open = (): void => {
    if (isOpen.value) {
      return;
    }

    updatePosition();
    isOpen.value = true;

    // Runs before the first paint: once the panel's real height is known, flip it upwards if it only fits there.
    nextTick(() => {
      if (isOpen.value && panelRef.value) {
        updatePosition(panelRef.value.scrollHeight);
      }
    });

    document.addEventListener("pointerdown", onDocumentPointerDown, true);
    window.addEventListener("scroll", onViewportChange, true);
    window.addEventListener("resize", onViewportChange);
  };

  const close = (): void => {
    if (!isOpen.value) {
      return;
    }

    isOpen.value = false;

    document.removeEventListener("pointerdown", onDocumentPointerDown, true);
    window.removeEventListener("scroll", onViewportChange, true);
    window.removeEventListener("resize", onViewportChange);
  };

  const toggle = (): void => {
    if (isOpen.value) {
      close();
    } else {
      open();
    }
  };

  onBeforeUnmount(close);

  return { rootRef, triggerRef, panelRef, isOpen, panelStyle, open, close, toggle };
};
